import { useEffect } from "react";
import api from "@/lib/api";

/* Lightweight global click-event capture.
 *
 * Any element on the site that has `data-analytics="some-event"` gets
 * its click recorded once, with the current page path and an optional
 * human-readable label (`data-analytics-label`).
 *
 * Usage in JSX:
 *   <a data-analytics="cta-get-funded" data-analytics-label="Hero">…</a>
 *
 * Tracking is fire-and-forget · beacons never block navigation. */
function findTagged(el) {
  let cur = el;
  while (cur && cur.nodeType === 1) {
    if (cur.dataset && cur.dataset.analytics) return cur;
    cur = cur.parentElement;
  }
  return null;
}

export default function ClickTracker() {
  useEffect(() => {
    const onClick = (e) => {
      const tagged = findTagged(e.target);
      if (!tagged) return;
      // Use sendBeacon when available so the request still completes
      // even if the user navigates away immediately.
      const payload = {
        event: tagged.dataset.analytics,
        path: window.location.pathname,
        label:
          tagged.dataset.analyticsLabel ||
          (tagged.innerText || "").trim().slice(0, 80) ||
          null,
      };
      try {
        const url = `${api.defaults.baseURL}/track-click`;
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });
        if (navigator.sendBeacon?.(url, blob)) return;
      } catch (_e) {
        /* fall through */
      }
      api.post("/track-click", payload).catch(() => {});
    };
    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);
  return null;
}
